<!-- Header -->
<div class="d-flex flex-wrap flex-stack mb-6">
  <div>
    <h3 class="fw-bold my-2">{{ 'companyProfile.billing.title' | translate }}</h3>
    <p class="text-muted fs-6 mb-0">{{ 'companyProfile.billing.subtitle' | translate }}</p>
  </div>
</div>

<!-- Error Alert -->
@if (error) {
  <div class="alert alert-danger d-flex align-items-center mb-6">
    <i class="fa fa-exclamation-circle me-2"></i>
    <span>{{ error }}</span>
    <button type="button" class="btn-close ms-auto" (click)="error = null"></button>
  </div>
}

<!-- Loading State -->
@if (loading) {
  <div class="card mb-9">
    <div class="card-body text-center py-15">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'companyProfile.billing.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'companyProfile.billing.loadingSettings' | translate }}</p>
    </div>
  </div>
}

<!-- Content -->
@if (!loading) {
  <div class="row g-6 mb-9">
    <!-- Stripe Connect Card -->
    <div class="col-12">
      <div class="card">
        <div class="card-header border-0">
          <h4 class="card-title m-0">
            <i class="fab fa-stripe me-2"></i>
            {{ 'companyProfile.billing.stripeConnect' | translate }}
          </h4>
          <span class="badge {{ getStatusBadgeClass() }}">{{ getStatusLabel() }}</span>
        </div>
        <div class="card-body">
          <!-- Not Connected State -->
          @if (!connectStatus || connectStatus.status === 'not_connected') {
            <div class="stripe-setup-card">
              <div class="stripe-icon">
                <i class="fab fa-stripe-s"></i>
              </div>
              <h5>{{ 'companyProfile.billing.connectStripe' | translate }}</h5>
              <p class="text-muted mb-4">
                {{ 'companyProfile.billing.connectDescription' | translate }}
              </p>
              <ul class="features-list mb-4">
                <li><i class="fa fa-check text-success me-2"></i>{{ 'companyProfile.billing.feature1' | translate }}</li>
                <li><i class="fa fa-check text-success me-2"></i>{{ 'companyProfile.billing.feature2' | translate }}</li>
                <li><i class="fa fa-check text-success me-2"></i>{{ 'companyProfile.billing.feature3' | translate }}</li>
                <li><i class="fa fa-check text-success me-2"></i>{{ 'companyProfile.billing.feature4' | translate }}</li>
              </ul>
              @if (isAdmin()) {
                <!-- Account Type Selection -->
                <div class="account-type-selection mb-4">
                  <label class="form-label fw-semibold">{{ 'companyProfile.billing.accountTypeLabel' | translate }}</label>
                  <div class="row g-3">
                    <div class="col-md-6">
                      <div
                        class="account-type-card"
                        [class.selected]="selectedBusinessType === 'individual'"
                        (click)="selectedBusinessType = 'individual'"
                      >
                        <div class="account-type-icon">
                          <i class="fa fa-user"></i>
                        </div>
                        <h6>{{ 'companyProfile.billing.individual' | translate }}</h6>
                        <p class="text-muted fs-7 mb-0">
                          {{ 'companyProfile.billing.individualDesc' | translate }}
                        </p>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div
                        class="account-type-card"
                        [class.selected]="selectedBusinessType === 'company'"
                        (click)="selectedBusinessType = 'company'"
                      >
                        <div class="account-type-icon">
                          <i class="fa fa-building"></i>
                        </div>
                        <h6>{{ 'companyProfile.billing.business' | translate }}</h6>
                        <p class="text-muted fs-7 mb-0">
                          {{ 'companyProfile.billing.businessDesc' | translate }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <app-button
                  [variant]="'primary'"
                  [label]="'companyProfile.billing.connectStripe' | translate"
                  [iconClass]="'fab fa-stripe'"
                  [loading]="actionLoading"
                  (clicked)="startOnboarding()"
                />
              } @else {
                <p class="text-muted">
                  <i class="fa fa-info-circle me-1"></i>
                  {{ 'companyProfile.billing.contactAdmin' | translate }}
                </p>
              }
            </div>
          }

          <!-- Onboarding In Progress State -->
          @if (connectStatus?.status === 'onboarding' || connectStatus?.status === 'pending') {
            <div class="stripe-status-card status-onboarding">
              <div class="status-icon warning">
                <i class="fa fa-clock"></i>
              </div>
              <h5>{{ 'companyProfile.billing.setupInProgress' | translate }}</h5>
              <p class="text-muted mb-4">
                {{ 'companyProfile.billing.setupDescription' | translate }}
              </p>
              @if (!connectStatus?.charges_enabled) {
                <div class="alert alert-warning mb-4">
                  <i class="fa fa-exclamation-triangle me-2"></i>
                  {{ 'companyProfile.billing.paymentsNotEnabled' | translate }}
                </div>
              }
              @if (isAdmin()) {
                <app-button
                  [variant]="'primary'"
                  [label]="'companyProfile.billing.continueSetup' | translate"
                  [iconClass]="'fa fa-arrow-right'"
                  [loading]="actionLoading"
                  (clicked)="continueOnboarding()"
                />
              }
            </div>
          }

          <!-- Connected State -->
          @if (connectStatus?.status === 'active') {
            <div class="stripe-status-card status-connected">
              <div class="status-icon success">
                <i class="fa fa-check"></i>
              </div>
              <h5>{{ 'companyProfile.billing.stripeConnected' | translate }}</h5>
              <p class="text-muted mb-4">
                {{ 'companyProfile.billing.stripeActiveDescription' | translate }}
              </p>

              <div class="row g-4 mb-4">
                <div class="col-md-6">
                  <div class="info-card">
                    <span class="info-label">{{ 'companyProfile.billing.chargesEnabled' | translate }}</span>
                    <span class="info-value">
                      @if (connectStatus?.charges_enabled) {
                        <i class="fa fa-check-circle text-success"></i> {{ 'common.yes' | translate }}
                      } @else {
                        <i class="fa fa-times-circle text-danger"></i> {{ 'common.no' | translate }}
                      }
                    </span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="info-card">
                    <span class="info-label">{{ 'companyProfile.billing.payoutsEnabled' | translate }}</span>
                    <span class="info-value">
                      @if (connectStatus?.payouts_enabled) {
                        <i class="fa fa-check-circle text-success"></i> {{ 'common.yes' | translate }}
                      } @else {
                        <i class="fa fa-times-circle text-danger"></i> {{ 'common.no' | translate }}
                      }
                    </span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="info-card">
                    <span class="info-label">{{ 'companyProfile.billing.platformFee' | translate }}</span>
                    <span class="info-value">{{ getPlatformFeeDisplay() }}</span>
                  </div>
                </div>
                @if (connectStatus?.onboarding_completed_at) {
                  <div class="col-md-6">
                    <div class="info-card">
                      <span class="info-label">{{ 'companyProfile.billing.connectedSince' | translate }}</span>
                      <span class="info-value">{{ connectStatus?.onboarding_completed_at | date:'mediumDate' }}</span>
                    </div>
                  </div>
                }
              </div>

              @if (isAdmin()) {
                <app-button
                  [variant]="'neutral'"
                  [label]="'companyProfile.billing.openDashboard' | translate"
                  [iconClass]="'fa fa-external-link-alt'"
                  [loading]="actionLoading"
                  (clicked)="openDashboard()"
                />
              }
            </div>
          }

          <!-- Restricted State -->
          @if (connectStatus?.status === 'restricted') {
            <div class="stripe-status-card status-restricted">
              <div class="status-icon danger">
                <i class="fa fa-exclamation-triangle"></i>
              </div>
              <h5>{{ 'companyProfile.billing.accountRestricted' | translate }}</h5>
              <p class="text-muted mb-4">
                {{ 'companyProfile.billing.restrictedDescription' | translate }}
              </p>
              @if (isAdmin()) {
                <div class="d-flex gap-2">
                  <app-button
                    [variant]="'danger'"
                    [label]="'companyProfile.billing.resolveIssues' | translate"
                    [iconClass]="'fa fa-external-link-alt'"
                    [loading]="actionLoading"
                    (clicked)="openDashboard()"
                  />
                  <app-button
                    [variant]="'neutral'"
                    [label]="'companyProfile.billing.refreshStatus' | translate"
                    [iconClass]="'fa fa-sync'"
                    (clicked)="loadConnectStatus()"
                  />
                </div>
              }
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Default Currency Card -->
    <div class="col-12">
      <div class="card">
        <div class="card-header border-0">
          <h4 class="card-title m-0">
            <i class="fa fa-coins me-2"></i>
            {{ 'companyProfile.billing.currencyTitle' | translate }}
          </h4>
        </div>
        <div class="card-body">
          <p class="text-muted mb-4">
            {{ 'companyProfile.billing.currencyDescription' | translate }}
          </p>

          <div class="row align-items-end">
            <div class="col-md-6 col-lg-4">
              <app-selectorini
                [options]="currencyOptions"
                [displayKey]="'name'"
                [valueKey]="'code'"
                [(value)]="selectedCurrency"
                [label]="'companyProfile.billing.defaultCurrency' | translate"
                [placeholder]="'companyProfile.billing.selectCurrency' | translate"
                [disabled]="!isAdmin()"
              />
            </div>
            <div class="col-auto mt-3 mt-md-0">
              @if (isAdmin()) {
                <app-button
                  [variant]="'primary'"
                  [label]="'companyProfile.billing.saveCurrency' | translate"
                  [iconClass]="'fa fa-save'"
                  [loading]="savingCurrency"
                  (clicked)="saveCurrency()"
                />
              }
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Payment Settings Card (when Stripe NOT connected) -->
    @if (!isStripeConnected()) {
      <div class="col-12">
        <div class="card">
          <div class="card-header border-0">
            <h4 class="card-title m-0">
              <i class="fa fa-money-bill-wave me-2"></i>
              {{ 'companyProfile.billing.customPaymentTitle' | translate }}
            </h4>
          </div>
          <div class="card-body">
            <p class="text-muted mb-4">
              {{ 'companyProfile.billing.customPaymentDescription' | translate }}
            </p>

            <!-- Enable Custom Payment Toggle -->
            <div class="form-check form-switch mb-4">
              <input
                class="form-check-input"
                type="checkbox"
                id="customPaymentEnabled"
                [(ngModel)]="customPaymentEnabled"
                [disabled]="!isAdmin()"
              />
              <label class="form-check-label fw-semibold" for="customPaymentEnabled">
                {{ 'companyProfile.billing.enableCustomPayment' | translate }}
              </label>
            </div>

            @if (customPaymentEnabled) {
              <!-- Payment Method Name -->
              <div class="mb-4">
                <label class="form-label fw-semibold">
                  {{ 'companyProfile.billing.paymentMethodName' | translate }}
                </label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="customPaymentMethodName"
                  [placeholder]="'companyProfile.billing.paymentMethodNamePlaceholder' | translate"
                  [disabled]="!isAdmin()"
                />
                <small class="text-muted">
                  {{ 'companyProfile.billing.paymentMethodNameHint' | translate }}
                </small>
              </div>

              <!-- Payment Instructions -->
              <div class="mb-4">
                <label class="form-label fw-semibold">
                  {{ 'companyProfile.billing.paymentInstructions' | translate }}
                </label>
                <textarea
                  class="form-control"
                  rows="4"
                  [(ngModel)]="customPaymentInstructions"
                  [placeholder]="'companyProfile.billing.paymentInstructionsPlaceholder' | translate"
                  [disabled]="!isAdmin()"
                ></textarea>
                <small class="text-muted">
                  {{ 'companyProfile.billing.paymentInstructionsHint' | translate }}
                </small>
              </div>

              @if (isAdmin()) {
                <app-button
                  [variant]="'primary'"
                  [label]="'companyProfile.billing.saveCustomPayment' | translate"
                  [iconClass]="'fa fa-save'"
                  [loading]="savingCustomPayment"
                  (clicked)="saveCustomPaymentSettings()"
                />
              }
            }

            @if (customPaymentEnabled) {
              <div class="alert alert-info mt-4 mb-0">
                <i class="fa fa-info-circle me-2"></i>
                {{ 'companyProfile.billing.customPaymentNote' | translate }}
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- Information Card -->
    <div class="col-12">
      <div class="card bg-light-primary">
        <div class="card-body">
          <div class="d-flex align-items-start">
            <div class="info-icon me-4">
              <i class="fa fa-info-circle text-primary"></i>
            </div>
            <div>
              <h5 class="mb-2">{{ 'companyProfile.billing.howPaymentsWork' | translate }}</h5>
              <p class="text-muted mb-0">
                {{ 'companyProfile.billing.howPaymentsWorkDescription' | translate }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Service Orders / Invoices (only for stables) -->
    @if (connectStatus?.status === 'active' && company.company_type === 'stable') {
      <div class="col-12">
        <div class="card">
          <div class="card-header border-0">
            <div class="d-flex flex-wrap justify-content-between align-items-center w-100">
              <div>
                <h4 class="card-title m-0">
                  <i class="fa fa-file-invoice me-2"></i>
                  {{ 'companyProfile.billing.serviceInvoices' | translate }}
                </h4>
                <p class="text-muted mb-0 mt-1">{{ 'companyProfile.billing.serviceInvoicesSubtitle' | translate }}</p>
              </div>
              <div class="d-flex gap-2 align-items-center">
                <select class="form-select form-select-sm" [(ngModel)]="ordersStatusFilter" (change)="onStatusFilterChange()" style="width: auto;">
                  <option value="">{{ 'companyProfile.billing.allStatus' | translate }}</option>
                  <option value="pending">{{ 'companyProfile.billing.statusPendingFilter' | translate }}</option>
                  <option value="paid">{{ 'companyProfile.billing.statusPaid' | translate }}</option>
                  <option value="cancelled">{{ 'companyProfile.billing.statusCancelled' | translate }}</option>
                </select>
                <app-button
                  [variant]="'primary'"
                  [label]="'companyProfile.billing.createInvoice' | translate"
                  [iconClass]="'fa fa-plus'"
                  (clicked)="openCreateInvoiceModal()"
                />
              </div>
            </div>
          </div>
          <div class="card-body">
            @if (loadingOrders) {
              <div class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
                </div>
              </div>
            } @else if (orders.length === 0) {
              <div class="text-center py-5">
                <i class="fa fa-file-invoice text-muted fs-1 mb-3 d-block"></i>
                <p class="text-muted">{{ 'companyProfile.billing.noInvoices' | translate }}</p>
              </div>
            } @else {
              <div class="table-responsive">
                <table class="table table-row-bordered align-middle gy-4">
                  <thead>
                    <tr class="fw-bold text-muted bg-light">
                      <th class="ps-4 rounded-start">{{ 'companyProfile.billing.tableUser' | translate }}</th>
                      <th>{{ 'companyProfile.billing.tableType' | translate }}</th>
                      <th>{{ 'companyProfile.billing.tableItems' | translate }}</th>
                      <th class="text-end">{{ 'companyProfile.billing.tableAmount' | translate }}</th>
                      <th class="text-center">{{ 'companyProfile.billing.tableStatus' | translate }}</th>
                      <th class="text-center">{{ 'companyProfile.billing.tableDate' | translate }}</th>
                      <th class="text-end rounded-end pe-4">{{ 'companyProfile.billing.tableActions' | translate }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (order of orders; track order.order_id) {
                      <tr>
                        <td class="ps-4">
                          @if (order.user) {
                            <div>
                              <span class="fw-bold">{{ order.user.full_name || order.user.first_name + ' ' + order.user.last_name }}</span>
                              <br />
                              <small class="text-muted">{{ order.user.email }}</small>
                            </div>
                          } @else {
                            <span class="text-muted">-</span>
                          }
                        </td>
                        <td>
                          <span class="badge badge-light-secondary">{{ getPaymentTypeLabel(order.payment_type) }}</span>
                        </td>
                        <td>
                          @if (order.order_lines && order.order_lines.length > 0) {
                            <span>{{ order.order_lines[0].description }}</span>
                            @if (order.order_lines.length > 1) {
                              <span class="text-muted"> +{{ order.order_lines.length - 1 }} more</span>
                            }
                          }
                        </td>
                        <td class="text-end">
                          <span class="fw-bold">{{ formatPrice(order.total_cents) }} {{ order.currency }}</span>
                        </td>
                        <td class="text-center">
                          <span class="badge {{ getOrderStatusClass(order.status) }}">{{ order.status | titlecase }}</span>
                        </td>
                        <td class="text-center">
                          <span class="text-muted">{{ order.created_at | date:'mediumDate' }}</span>
                        </td>
                        <td class="text-end pe-4">
                          <div class="d-flex justify-content-end gap-1">
                            <app-button
                              [variant]="'ghost'"
                              [iconClass]="'fa fa-eye text-primary'"
                              [size]="'sm'"
                              (clicked)="viewInvoice(order)"
                            />
                            @if (order.status === 'pending') {
                              <app-button
                                [variant]="'ghost'"
                                [iconClass]="'fa fa-times text-danger'"
                                [size]="'sm'"
                                (clicked)="cancelOrder(order)"
                              />
                            }
                          </div>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        </div>
      </div>
    }
  </div>
}
